import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { toTitleCase } from "../../../../utils";
import useDepartmentSelector from "../hooks/useDepartmentSelector";

const getApplicationData = (t, id = null, userUid) => {
  const tenantId = Digit.ULBService.getStateId() || 'hp';
  const edcrData = Digit.SessionStorage.get("EDCR_CREATE")?.[0];
  const planDetail = edcrData?.planDetail;
  const isRescrutiny = planDetail?.applicationScrutinyType || false
  const regulatedObj = edcrData?.planDetail?.reportOutput?.scrutinyDetails?.filter((e) => e.key === 'Block_1_Side Setback')
  const { isSuccess: isMdmsSuccess, data: mdmsData } = Digit.Hooks.obps.useMDMS(tenantId, "BPA", ["RiskTypeComputation"]);
  const calcRiskType = isMdmsSuccess && (
    Digit.Utils.obps.calculateRiskType(
      mdmsData?.BPA?.RiskTypeComputation,
      planDetail?.plot?.area,
      planDetail?.blocks
    )
  );
  const riskType = calcRiskType ? t(`WF_BPA_${calcRiskType}`) : null;
  const scrutinyDetails = Digit.SessionStorage.get("ScrutinyDetails");
  const blocks = planDetail?.blocks?.[0];
  const setBacksData = blocks?.setBacks?.[0];
  const planInformation = planDetail?.planInformation;
  const currentCity = JSON.parse(sessionStorage.getItem("currentCity") || null);
  const currLocality = JSON.parse(sessionStorage.getItem("currLocality") || null);
  const localityName = currLocality?.localname?.trim() || '';

  if (id && !isRescrutiny) { // Fetch application data for edit flow
    const fetchApplicationData = async () => {
      return await Digit.OBPSService.BPASearch(tenantId, { applicationNo: id, isEmployee: false })
    }
    const { data, isLoading: isAppDataLoading, isSuccess } = useQuery({ queryKey: [id], queryFn: fetchApplicationData, enabled: isMdmsSuccess });
    const applicationData = isSuccess && data?.BPA?.[0];
    return { applicationData, isAppDataLoading }
  }
  else if (id && isRescrutiny) {
    const fetchApplicationData = async () => {
      return await Digit.OBPSService.BPASearch(tenantId, { applicationNo: id, isEmployee: false })
    }
    const { data, isLoading: isAppDataLoading, isSuccess } = useQuery({ queryKey: [id], queryFn: fetchApplicationData, enabled: isMdmsSuccess });
    let applicationData = isSuccess && data?.BPA?.[0];
    if (applicationData) {
      const applicantName = toTitleCase(scrutinyDetails?.applicantName || planInformation?.applicantName);
      const existingOwner = applicationData?.landInfo?.owners[0];
      const existingChecklist = applicationData?.landInfo?.checklistdata;
      const modifiedOwner = {
        ...existingOwner,
        name: applicantName,
        fatherOrHusbandName: toTitleCase(planInformation?.applicantGuardianName),
        mobileNumber: planInformation?.applicantMobile || existingOwner.mobileNumber,
        emailId: planInformation?.applicantEmail || existingOwner.emailId,
        gender: existingOwner.gender, // preserving the original gender value
        correspondenceAddress: {
          ...existingOwner.correspondenceAddress,
          applicantName,
          applicantGuardianName: toTitleCase(planInformation?.applicantGuardianName),
          state: toTitleCase(planInformation?.correspondenceAddress?.state),
          district: toTitleCase(planInformation?.correspondenceAddress?.district),
          tehsil: toTitleCase(planInformation?.correspondenceAddress?.tehsil),
          town: toTitleCase(planInformation?.correspondenceAddress?.town),
          address: planInformation?.correspondenceAddress?.address || existingOwner.correspondenceAddress.address,
          pinno: planInformation?.correspondenceAddress?.pinCode || existingOwner.correspondenceAddress.pinno,
          type: "CORRESPONDENCE",
        },
        permanentAddress: {
          ...existingOwner.permanentAddress,
          applicantName,
          applicantGuardianName: toTitleCase(planInformation?.applicantGuardianName),
          state: toTitleCase(planInformation?.permanentAddress?.state),
          district: toTitleCase(planInformation?.permanentAddress?.district),
          tehsil: toTitleCase(planInformation?.permanentAddress?.tehsil),
          town: toTitleCase(planInformation?.permanentAddress?.town),
          address: planInformation?.permanentAddress?.address || existingOwner.permanentAddress.address,
          pinno: planInformation?.permanentAddress?.pinCode || existingOwner.permanentAddress.pinno,
          type: "PERMANENT",
        },
      };
      const modifiedChecklist = {
        ...existingChecklist,
        plotAreaForSubdivision: planInformation?.plotArea || existingChecklist?.plotAreaForSubdivision,
        ownershipRevenueDocuments: existingChecklist?.ownershipRevenueDocuments || 'notSubmitted',
        landUse: toTitleCase(planInformation?.landUseZone) || existingChecklist?.landUse,
        permissableFar: planDetail?.farDetails?.permissableFar || existingChecklist?.permissableFar || 0,
        maxFloor: planDetail?.blocks?.[0]?.building?.maxFloor || existingChecklist?.maxFloor || 0,
        far: planDetail?.farDetails?.providedFar || existingChecklist?.far || 0,
        builtupArea: planDetail?.virtualBuilding?.totalBuitUpArea || existingChecklist?.builtupArea || null,
        totalCoverageArea: planDetail?.virtualBuilding?.totalCoverageArea || existingChecklist?.totalCoverageArea || null,
        isNHSetBacks: existingChecklist?.isNHSetBacks || 'yes',
        setBacks: {
          front: setBacksData?.frontYard?.mean || existingChecklist?.setBacks?.front,
          rear: setBacksData?.rearYard?.mean || existingChecklist?.setBacks?.rear,
          left: setBacksData?.sideYard1?.mean || existingChecklist?.setBacks?.left,
          right: setBacksData?.sideYard2?.mean || existingChecklist?.setBacks?.right,
        },
        regulatedsetBacks: {
          front: regulatedObj?.[0]?.detail?.[0]?.Permissible || existingChecklist?.regulatedsetBacks?.front,
          rear: regulatedObj?.[0]?.detail?.[1]?.Permissible || existingChecklist?.regulatedsetBacks?.rear,
          left: regulatedObj?.[0]?.detail?.[2]?.Permissible || existingChecklist?.regulatedsetBacks?.left,
          right: regulatedObj?.[0]?.detail?.[3]?.Permissible || existingChecklist?.regulatedsetBacks?.right,
        },
        setBacks_relaxation: existingChecklist?.setBacks_relaxation || false,
        numberOfStoreys: planDetail?.blocks?.[0]?.building?.totalFloors || existingChecklist?.numberOfStoreys,
        shownInTatima: existingChecklist?.shownInTatima || 'notSubmitted',
        whetherHtLtLineCrossing: existingChecklist?.whetherHtLtLineCrossing || 'no',
        whetherNearbyAsiMonument: planInformation?.buildingNearMonument || existingChecklist?.whetherNearbyAsiMonument || 'no',
        whetherNearbyHfl: existingChecklist?.whetherNearbyHfl || 'no',
        whetherTreesOnPlot: existingChecklist?.whetherTreesOnPlot || 'no',
        whetherAbuttingForestBoundary: existingChecklist?.whetherAbuttinorestBoundary || 'no',
        whetherAbuttingHighway: existingChecklist?.whetherAbuttingHighway || 'no',
        whetherAbuttingNaturalDrainage: planInformation?.buildingNearToRiver || existingChecklist?.whetherAbuttingNaturalDrainage || 'no',
        whetherSeweragePassingThrough: existingChecklist?.whetherSeweragePassingThrough || 'no',
        whetherAboveRoadLevel: existingChecklist?.whetherAboveRoadLevel || 'no',
        whetherSlidingArea: existingChecklist?.whetherSlidingArea || 'no',
        whetherCommunicationTower: existingChecklist?.whetherCommunicationTower || 'no',
        whetherGroupHousing: existingChecklist?.whetherGroupHousing || 'no',
        whetherBuildingLandFill: existingChecklist?.whetherBuildingLandFill || 'no',
        whetherRaisedReclaimed: existingChecklist?.whetherRaisedReclaimed || 'no',
        whetherAirportPermissionRequired: planInformation?.nocNearAirport || existingChecklist?.whetherAirportPermissionRequired || 'no',
        provisionForDisabiled: planInformation?.barrierFreeAccessForPhyChlngdPpl || existingChecklist?.provisionForDisabiled || null,
        provisionRainWaterHarvesting: planDetail?.utility?.rainWaterHarvest?.tankCapacity || existingChecklist?.provisionRainWaterHarvesting || null,
        heightOfSloppingRoof: planDetail?.blocks?.[0]?.maxSloppingRoofHeight || existingChecklist?.heightOfSloppingRoof || null,
        heightOfFloor: planDetail?.blocks?.[0]?.floors?.[0]?.floorHeights || existingChecklist?.heightOfFloor || null,
        heightOfBuilding: planDetail?.blocks?.[0]?.building?.buildingHeight || existingChecklist?.heightOfBuilding || null,
        distanceBetween2Blocks: planDetail?.blocks?.[0]?.distanceBetween2Blk || existingChecklist?.distanceBetween2Blocks || null,
        anyOtherInformation: planDetail?.virtualBuilding?.totalCoverageArea || existingChecklist?.anyOtherInformation || null,
      };
      const modifiedLandInfo = {
        ...applicationData.landInfo,
        owners: [modifiedOwner],
        checklistdata: modifiedChecklist,
        address: {
          city: currentCity?.name,
          locality: { code: currLocality?.code },
          region: localityName,
          doorNo: currLocality?.name,
          areaType: scrutinyDetails?.areaType,
        },
      };
      applicationData = {
        ...applicationData,
        applicationNo: edcrData?.applicationNumber || null,
        applicationDate: edcrData?.applicationDate || null,
        applicationSubType: edcrData?.applicationSubType || null,
        occupancy: planInformation?.occupancy || null,
        edcrNumber: edcrData?.edcrNumber || null,
        totalBuildUpArea: planDetail?.virtualBuilding?.totalBuitUpArea,
        maximumPermissibleGroundCoverage: planDetail?.virtualBuilding?.totalCoverageArea?.toFixed(2) || null,
        typeofArea: toTitleCase(planInformation?.landUseZone),
        propertyType: toTitleCase(planInformation?.applicantPropertyType || planDetail?.planInfoProperties?.PROPERTY_TYPE),
        proposal: planInformation?.proposalSubmittedFor || null,
        revenueVillageMohal: planInformation?.mauza || null,
        khataNo: planInformation?.khataNo || null,
        khatauniNo: planInformation?.khatauniNo || null,
        plotArea: planInformation?.plotArea || planDetail?.plot?.area || 0,
        totalPlotArea: planInformation?.plotArea || 0,
        blocks: planDetail?.blocks || null,
        noOfBlocks: planDetail?.blocks?.length || 0,
        premiumFAR: planDetail?.virtualBuilding?.premiumFarArea || 0,
        demolitionArea: planInformation?.demolitionArea || null,
        parkingArea: planDetail?.parkingDetails?.overAllParkingArea || 0,
        parkingFloorNo: planDetail?.parkingDetails?.parkingFloorNo || 0,
        activeTab: Digit?.SessionStorage.get("FORM12_CURRENT_TAB") || 0,
        landInfo: modifiedLandInfo,
      };
    }
    return { applicationData, isAppDataLoading }
  }


  // Create Application data object for create flow
  const applicationType = "FORM_12";
  const userInfo = Digit.UserService.getUser();
  const { documents } = useSelector(state => state.form12);
  const [isUdFlow, applicationFlow] = useDepartmentSelector();

  // const fetchBusinessService = async () => {
  //   return await Digit.LocationService.getBusinessServices(currentCity?.name, localityName, applicationType, applicationFlow)
  // }
  // const { isLoading: isBizServiceLoading, data: bizServiceData, isSuccess } = useQuery({
  //   queryKey: ['businessService'], queryFn: fetchBusinessService, enabled: isMdmsSuccess
  // });
  // const bizServiceFallback = `BPA_${applicationFlow}${(localityName).toLocaleUpperCase()}`;
  // const businessService = (!isBizServiceLoading && isSuccess) ? bizServiceData : (
  //   isUdFlow ? bizServiceFallback : (currLocality?.IsSpecialArea ? bizServiceFallback : 'BPA')
  // );
  const applicantName = toTitleCase(scrutinyDetails?.applicantName || planInformation?.applicantName);

  const checklistdata = {
    typeOfArea: null,
    typeOfArea_relaxation: null,
    plotAreaForSubdivision: planInformation?.plotArea || null,
    regulatedPlotArea: null, //JSON

    ownershipRevenueDocuments: 'notSubmitted',
    landUse: toTitleCase(planInformation?.landUseZone),
    regulatedLandUse: null,
    permissableFar: planDetail?.farDetails?.permissableFar || 0,
    maxFloor: planDetail?.blocks?.[0]?.building?.maxFloor || 0,
    far: planDetail?.farDetails?.providedFar || 0,
    regulatedFAR: null, //JSON
    builtupArea: planDetail?.virtualBuilding?.totalBuitUpArea || null,
    regulatedBuildUpArea: null,
    totalCoverageArea: planDetail?.virtualBuilding?.totalCoverageArea || 0,
    isNHSetBacks: 'yes',
    setBacks: {
      front: setBacksData?.frontYard?.mean || "",
      rear: setBacksData?.rearYard?.mean || "",
      left: setBacksData?.sideYard1?.mean || "",
      right: setBacksData?.sideYard2?.mean || "",
    },
    regulatedsetBacks: {
      front: regulatedObj?.[0]?.detail?.[0]?.Permissible || "",
      rear: regulatedObj?.[0]?.detail?.[1]?.Permissible || "",
      left: regulatedObj?.[0]?.detail?.[2]?.Permissible || "",
      right: regulatedObj?.[0]?.detail?.[3]?.Permissible || "",
    },
    setBacks_relaxation: false,
    numberOfStoreys: planDetail?.blocks?.[0]?.building?.totalFloors || 0,
    shownInTatima: 'notSubmitted',
    whetherHtLtLineCrossing: 'no',
    whetherNearbyAsiMonument: planInformation?.buildingNearMonument || 'no',
    whetherNearbyHfl: 'no',
    whetherTreesOnPlot: 'no',
    whetherAbuttingForestBoundary: 'no',
    whetherAbuttingHighway: 'no',
    whetherAbuttingNaturalDrainage: planInformation?.buildingNearToRiver || 'no',
    whetherSeweragePassingThrough: 'no',
    whetherAboveRoadLevel: 'no',
    whetherSlidingArea: 'no',
    whetherCommunicationTower: 'no',
    whetherGroupHousing: 'no',
    whetherBuildingLandFill: 'no',
    whetherRaisedReclaimed: 'no',
    whetherAirportPermissionRequired: planInformation?.nocNearAirport || 'no',

    existingAtSite: null,
    regulatedexistingAtSite: null,
    parkingProvision: null,
    regulatedparkingProvision: null,
    nocFromHPSEB: null,
    noOfTrees: null,
    distanceOfTrees: null,
    distanceFromForestBoundary: null,
    nocFromNHAI: null,
    distanceFromNullah: null,
    distanceFromKhudHfl: null,
    provisionOfSolar: null,
    regulatedprovisionOfSolar: null,
    provisionForDisabiled: planInformation?.barrierFreeAccessForPhyChlngdPpl || null,
    regulatedprovisionForDisabiled: null,
    whetherAboveRoadLevel_view: null,
    whetherSlidingArea_doc: null,
    provisionRainWaterHarvesting: planDetail?.utility?.rainWaterHarvest?.tankCapacity || null,
    regulatedprovisionRainWaterHarvesting: null,
    structuralStabilityCertificate: null,
    numberOfDwellingUnits: null,
    regulatednumberOfDwellingUnits: null,
    slopeOfLandDegree: null,
    regulatedslopeOfLandDegree: null,
    lopeOfLandDegree_remarks: null,
    slopeOfLandDegree_relaxation: null,
    regulatedheightOfParkingFloor: null,
    heightOfParkingFloor: null,
    heightOfParkingFloor_remarks: null,
    heightOfParkingFloor_relaxation: null,
    heightOfSloppingRoof: planDetail?.blocks?.[0]?.maxSloppingRoofHeight || null,
    regulatedheightOfSloppingRoof: null,
    heightOfSloppingRoof_remarks: null,
    heightOfSloppingRoof_relaxation: null,
    heightOfFloor: planDetail?.blocks?.[0]?.floors?.[0]?.floorHeights || null,
    regulatedheightOfFloor: null,
    heightOfFloor_remarks: null,
    heightOfFloor_relaxation: null,
    heightOfBuilding: planDetail?.blocks?.[0]?.building?.buildingHeight || null,
    regulatedmaxHillCut: null,
    maxHillCut: null,
    maxHillCut_remarks: null,
    maxHillCut_relaxation: null,
    regulateddistanceBetween2Blocks: null,
    distanceBetween2Blocks: planDetail?.blocks?.[0]?.distanceBetween2Blk || null,
    distanceBetween2Blocks_relaxation: null,
    distanceBetween2Blocks_remarks: null,
    regulatedplinthHeight: null,
    plinthHeight: null,
    plinthHeight_remarks: null,
    plinthHeight_relaxation: null,
    regulatedheightOfServiceFloor: null,
    heightOfServiceFloor: null,
    heightOfServiceFloor_remarks: null,
    heightOfServiceFloor_relaxation: null,
    regulatedequivalentCarSpace: null,
    equivalentCarSpace: null,
    equivalentCarSpace_remarks: null,
    equivalentCarSpace_relaxation: null,
    regulatedbuildableWidth: null,
    buildableWidth: null,
    PhotographsOfSite: null,
    anyOtherInformation: planDetail?.virtualBuilding?.totalCoverageArea || null,

    whetherNearbyAsiMonument_noc: null,
    whetherNearbyHfl_noc: null,
    forestDepartment_noc: null,
    whetherSeweragePassingThrough_noc: null,
    whetherAirportPermissionRequired_noc: null,

    typeOfArea_remarks: null,
    plotAreaForSubdivision_remarks: null,
    plotAreaForSubdivision_relaxation: null,
    ownershipRevenueDocuments_remarks: null,
    ownershipRevenueDocuments_relaxation: null,
    landUse_remarks: null,
    landUse_relaxation: null,
    far_remarks: null,
    far_relaxation: null,
    builtupArea_remarks: null,
    builtupArea_relaxation: null,
    setBacks_remarks: null,
    nhsetBacks_remarks: null,
    setBacks_relaxation: null,
    nhsetBacks_relaxation: null,
    numberOfStoreys_remarks: null,
    roadPathExisting_remarks: null,
    roadPathExisting_relaxation: null,
    parkingProvision_remarks: null,
    parkingProvision_relaxation: null,
    nocFromHPSEB_remarks: null,
    nocFromHPSEB_relaxation: null,
    whetherNearbyAsiMonument_remarks: null,
    whetherNearbyAsiMonument_relaxation: null,
    whetherNearbyHflc_remarks: null,
    whetherNearbyHflc_relaxation: null,
    whetherTreesOnPlot_remarks: null,
    whetherTreesOnPlot_relaxation: null,
    whetherAbuttingHighway_remarks: null,
    whetherAbuttingHighway_relaxation: null,
    whetherAbuttingNaturalDrainage_remarks: null,
    whetherAbuttingNaturalDrainage_relaxation: null,
    whetherSeweragePassingThrough_remarks: null,
    whetherSeweragePassingThrough_relaxation: null,
    provisionOfSolar_remarks: null,
    provisionOfSolar_relaxation: null,
    provisionForDisabiled_remarks: null,
    provisionForDisabiled_relaxation: null,
    whetherAboveRoadLevel_remarks: null,
    whetherAboveRoadLevel_relaxation: null,
    whetherSlidingArea_remarks: null,
    whetherSlidingArea_relaxation: null,
    provisionRainWaterHarvesting_remarks: null,
    provisionRainWaterHarvesting_relaxation: null,
    structuralStabilityCertificate_remarks: null,
    structuralStabilityCertificate_relaxation: null,
    numberOfDwellingUnits_remarks: null,
    numberOfDwellingUnits_relaxation: null,
    whetherCommunicationTower_remarks: null,
    whetherCommunicationTower_relaxation: null,
    whetherGroupHousing_remarks: null,
    whetherCommunicationTower_relaxation: null,
    whetherGroupHousing_relaxation: null,
    whetherBuildingLandFill_remarks: null,
    whetherBuildingLandFill_relaxation: null,
    whetherRaisedReclaimed_remarks: null,
    whetherRaisedReclaimed_relaxation: null,
    riskBasedClassification_remarks: null,
    slopeOfLandDegree_remarks: null,
    PhotographsOfSite_remarks: null,
    anyOtherInformation_remarks: null,
    anyOtherInformation_relaxation: null,
    whetherAirportPermissionRequired_remarks: null,
    whetherAirportPermissionRequired_relaxation: null,
  };

  const applicationData = {
    tenantId,
    applicationNo: edcrData?.applicationNumber || null,
    applicationDate: edcrData?.applicationDate || null,
    applicationSubType: edcrData?.applicationSubType || null,
    occupancy: planInformation?.occupancy || null,
    edcrNumber: edcrData?.edcrNumber || null,
    //totalBuildUpArea: planDetail?.virtualBuilding?.totalBuitUpArea,
    //totalProposedArea: planDetail?.virtualBuilding?.totalProposedArea,
    //totalExistingBuiltUpArea: planDetail?.virtualBuilding?.totalExistingBuiltUpArea,
    //maximumPermissibleGroundCoverage: planDetail?.virtualBuilding?.totalCoverageArea?.toFixed(2) || null,
    //typeofArea: toTitleCase(planInformation?.landUseZone),
    //propertyType: toTitleCase(planInformation?.applicantPropertyType || planDetail?.planInfoProperties?.PROPERTY_TYPE),
    //proposal: planInformation?.proposalSubmittedFor || null,
    //revenueVillageMohal: planInformation?.mauza || null,
    //khataNo: planInformation?.khataNo || null,
    //khatauniNo: planInformation?.khatauniNo || null,
    //plotArea: planInformation?.plotArea || planDetail?.plot?.area || 0,
    //totalPlotArea: planInformation?.plotArea || 0,
    blocks: planDetail?.blocks || null,
    //noOfBlocks: planDetail?.blocks?.length || 0,
    premiumFAR: planDetail?.virtualBuilding?.premiumFarArea || 0,
    //demolitionArea: planInformation?.demolitionArea || null,
    //parkingArea: planDetail?.parkingDetails?.overAllParkingArea || 0,
    parkingFloorNo: planDetail?.parkingDetails?.parkingFloorNo || 0,
    activeTab: Digit?.SessionStorage.get("FORM12_CURRENT_TAB") || 0,
    riskType,
    applicationType,
    feeType: "ApplicationFee",
    workflow: { action: "INITIATE", assignes: [userUid] },
    landInfo: {
      owners: [{
        name: null,
        fatherOrHusbandName: null,
        mobileNumber: '',
        emailId: '',
        gender: null,
        correspondenceAddress: {
          applicantName,
          applicantGuardianName: null,
          state: 'Uttar Pradesh',
          district: null,
          tehsil: null,
          town: null,
          address: '',
          pinno: '',
          type: "CORRESPONDENCE",
        },
        permanentAddress: {
          applicantName,
          applicantGuardianName: null,
          state: 'Uttar Pradesh',
          district: null,
          tehsil: null,
          town: null,
          address: '',
          pinno: '',
          type: "PERMANENT",
        },
      }],
      address: {
        city: currentCity?.name,
        locality: { code: currLocality?.code },
        region: localityName,
        doorNo: currLocality?.name,
        areaType: scrutinyDetails?.areaType,
      },
      //checklistdata,
      tenantId
    },
    professionalInformation: {
      name: userInfo?.info?.name,
      mobileNumber: userInfo?.info?.mobileNumber,
    },
    landAreaDetails: {
      city: scrutinyDetails?.city?.name || toTitleCase(planInformation?.correspondenceAddress?.district) || null,
      locality: {
        name: scrutinyDetails?.locality?.name || toTitleCase(planInformation?.correspondenceAddress?.tehsil) || null,
      },
      village: {
        name: scrutinyDetails?.village?.name || toTitleCase(planInformation?.correspondenceAddress?.town) || null,
      },
      areaType: scrutinyDetails?.areaType
    },
    businessService: null,
    documents,
    uiFlow: { flow: 'Form12' },
  }

  return { applicationData }
}

export default getApplicationData;
